import { useMemo, useState } from "react";
import { formatDate } from "@/lib/date";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  CalendarDays,
  FolderOpen,
  Layers,
  Loader2,
  Package,
  PackageMinus,
  Search,
  ShieldCheck,
  User,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useCartridgeDetail, useCartridgeAssignments } from "@/hooks/useCartridgeDetail";
import { useProjects } from "@/hooks/useProjects";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { assignToProject, installCartridge, unassignFromProject } from "@/api/cartridges";
import { MemoryGraphD3 } from "@/components/memory/MemoryGraphD3";
import type { GraphSeedResponse } from "@/hooks/useMemoryGraph";
import type { CartridgeDetailResponse, CartridgeNodeResult } from "@/types/cartridge";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/ui/page-header";
import { VisibilityBadge } from "@/components/ui/visibility-badge";
import { EmptyState } from "@/components/ui/empty-state";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

function toGraphSeed(detail: CartridgeDetailResponse): GraphSeedResponse {
  return {
    nodes: detail.nodes.map((n) => ({
      node_id: n.node_id,
      node_type: n.node_type,
      label: n.scope,
      source_file: n.source_file,
      properties: n.properties,
      repo_id: null,
    })),
    edges: detail.edges.map((e) => ({
      source: e.source,
      target: e.target,
      edge_type: e.edge_type,
      weight: e.weight,
      properties: e.properties,
    })),
    total_nodes: detail.node_count,
    has_more: false,
  };
}

const TYPE_COLORS: Record<string, string> = {
  concept: "bg-blue-500",
  practice: "bg-green-500",
  principle: "bg-purple-500",
  pattern: "bg-amber-500",
  reference: "bg-rose-500",
  requirement: "bg-cyan-500",
  decision: "bg-orange-500",
  guardrail: "bg-red-500",
};

function groupByType(nodes: CartridgeNodeResult[]): Record<string, CartridgeNodeResult[]> {
  return nodes.reduce<Record<string, CartridgeNodeResult[]>>((acc, node) => {
    (acc[node.node_type] ??= []).push(node);
    return acc;
  }, {});
}

function PolicyBadge({ policy }: { policy: string }) {
  const isRequired = policy === "required";
  const classes = isRequired
    ? "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700"
    : "bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${classes}`}>
      <ShieldCheck className="h-3 w-3" />
      {policy}
    </span>
  );
}

export function CartridgeDetailPage() {
  const { t } = useTranslation();
  const { orgSlug, cartridgeName } = useParams<{ orgSlug: string; cartridgeName: string }>();
  const navigate = useNavigate();
  const { detail, loading, error, notInstalled, refetch } = useCartridgeDetail(cartridgeName ?? null);
  const { assignments, loading: assignmentsLoading, refetch: refetchAssignments } = useCartridgeAssignments(cartridgeName ?? null);
  const { projects } = useProjects();
  const [activeTab, setActiveTab] = useState("overview");
  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState("");
  const [policy, setPolicy] = useState<"required" | "optional">("optional");
  const [assigning, setAssigning] = useState(false);
  const [unassignTarget, setUnassignTarget] = useState<{ projectId: string; projectSlug: string } | null>(null);
  const [unassigning, setUnassigning] = useState(false);
  const [installingFromDetail, setInstallingFromDetail] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const [selectedGraphNode, setSelectedGraphNode] = useState<string | null>(null);

  const grouped = useMemo(
    () => (detail ? groupByType(detail.nodes) : {}),
    [detail],
  );

  const distinctTypes = Object.keys(grouped).length;

  const graphSeed = useMemo(
    () => (detail ? toGraphSeed(detail) : null),
    [detail],
  );

  const filteredNodes = useMemo(() => {
    if (!detail || !debouncedQuery) return detail?.nodes ?? [];
    const q = debouncedQuery.toLowerCase();
    return detail.nodes.filter(
      (n) =>
        n.scope.toLowerCase().includes(q) ||
        n.content.toLowerCase().includes(q),
    );
  }, [detail, debouncedQuery]);

  const assignedProjectIds = new Set(assignments.map((a) => a.project_id));
  const availableProjects = projects.filter((p) => !assignedProjectIds.has(p.id));

  async function handleAssign() {
    if (!cartridgeName || !selectedProject) return;
    setAssigning(true);
    try {
      await assignToProject(cartridgeName, selectedProject, policy);
      refetchAssignments();
      toast.success(t("cartridgeDetail.toast.assigned"));
      setAssignOpen(false);
      setSelectedProject("");
      setPolicy("optional");
    } catch {
      toast.error(t("cartridgeDetail.toast.assignFailed"));
    } finally {
      setAssigning(false);
    }
  }

  async function handleUnassign() {
    if (!cartridgeName || !unassignTarget) return;
    setUnassigning(true);
    try {
      await unassignFromProject(cartridgeName, unassignTarget.projectId);
      refetchAssignments();
      toast.success(t("cartridgeDetail.toast.unassigned", { slug: unassignTarget.projectSlug }));
      setUnassignTarget(null);
    } catch {
      toast.error(t("cartridgeDetail.toast.unassignFailed"));
    } finally {
      setUnassigning(false);
    }
  }

  async function handleInstallFromDetail() {
    if (!cartridgeName || installingFromDetail) return;
    setInstallingFromDetail(true);
    try {
      await installCartridge(cartridgeName);
      toast.success(t("cartridges.toast.installed", { name: cartridgeName }));
      refetch();
    } catch {
      toast.error(t("cartridges.toast.installFailed", { name: cartridgeName }));
    } finally {
      setInstallingFromDetail(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (notInstalled) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={Package}
          title={cartridgeName ?? ""}
          leading={
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("cartridgeDetail.back")}
              onClick={() => navigate(`/${orgSlug}/cartridges`)}
              className="-ml-2 mt-0.5 shrink-0 hover:bg-primary/10"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          }
          secondaryGlow={false}
        />
        <EmptyState
          icon={Package}
          title={t("cartridgeDetail.notInstalled.title")}
          description={t("cartridgeDetail.notInstalled.description")}
          action={{
            label: installingFromDetail
              ? t("cartridgeDetail.notInstalled.installing")
              : t("cartridgeDetail.notInstalled.installButton"),
            onClick: handleInstallFromDetail,
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" aria-label={t("cartridgeDetail.back")} onClick={() => navigate(`/${orgSlug}/cartridges`)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (!detail) return null;

  const maxCount = Math.max(...Object.values(grouped).map((g) => g.length), 1);

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Package}
        title={detail.cartridge_name}
        leading={
          <Button
            variant="ghost"
            size="sm"
            aria-label={t("cartridgeDetail.back")}
            onClick={() => navigate(`/${orgSlug}/cartridges`)}
            className="-ml-2 mt-0.5 shrink-0 hover:bg-primary/10"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
        }
        titleExtra={
          <>
            <VisibilityBadge visibility={detail.visibility} />
            {detail.description && (
              <span className="text-sm text-muted-foreground">{detail.description}</span>
            )}
          </>
        }
        stats={[
          { value: detail.node_count, label: t("cartridgeDetail.overview.totalNodes"), icon: Layers },
          { value: distinctTypes, label: t("cartridgeDetail.overview.nodeTypes"), icon: Package },
        ]}
        secondaryGlow={false}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("cartridgeDetail.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="nodes">
            {t("cartridgeDetail.tabs.nodes")}
            {detail.node_count > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">{detail.node_count}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="graph">{t("cartridgeDetail.tabs.graph")}</TabsTrigger>
          <TabsTrigger value="assignments">{t("cartridgeDetail.tabs.assignments")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="space-y-6">
            <div>
              <h3 className="text-sm font-medium mb-3">{t("cartridgeDetail.overview.distributionTitle")}</h3>
              <div className="space-y-2">
                {Object.entries(grouped)
                  .sort(([, a], [, b]) => b.length - a.length)
                  .map(([type, nodes]) => (
                    <div key={type} className="flex items-center gap-3" data-testid={`distribution-${type}`}>
                      <span className="w-24 text-sm font-medium truncate">{type}</span>
                      <div className="flex-1 h-6 bg-muted rounded overflow-hidden">
                        <div
                          className={`h-full rounded ${TYPE_COLORS[type] ?? "bg-primary"}`}
                          style={{ width: `${(nodes.length / maxCount) * 100}%` }}
                        />
                      </div>
                      <span className="w-8 text-sm text-muted-foreground text-right">{nodes.length}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-3">{t("cartridgeDetail.overview.searchTitle")}</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("cartridgeDetail.overview.searchPlaceholder")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {debouncedQuery && (
                <p className="text-sm text-muted-foreground mt-2">
                  {t("cartridgeDetail.overview.matchCount", { count: filteredNodes.length, total: detail.nodes.length })}
                </p>
              )}
              {debouncedQuery && filteredNodes.length > 0 && (
                <div className="mt-3 space-y-1">
                  {filteredNodes.map((node) => (
                    <div key={node.node_id} className="flex items-center gap-2 rounded border px-3 py-2 text-sm">
                      <Badge variant="outline" className="text-xs">{node.node_type}</Badge>
                      <span className="font-medium">{node.scope}</span>
                      <span className="text-muted-foreground truncate">
                        {node.content.length > 80 ? `${node.content.slice(0, 80)}…` : node.content}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="nodes">
          {detail.nodes.length === 0 ? (
            <EmptyState
              icon={Package}
              title={t("cartridgeDetail.nodes.empty")}
              description={t("cartridgeDetail.nodes.emptyDescription")}
            />
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2 flex-wrap">
                <Button
                  size="sm"
                  variant={typeFilter === null ? "default" : "outline"}
                  onClick={() => setTypeFilter(null)}
                >
                  {t("cartridgeDetail.nodes.allFilter")}
                </Button>
                {Object.keys(grouped).sort().map((type) => (
                  <Button
                    key={type}
                    size="sm"
                    variant={typeFilter === type ? "default" : "outline"}
                    onClick={() => setTypeFilter(typeFilter === type ? null : type)}
                    data-testid={`filter-${type}`}
                    className="gap-1.5"
                  >
                    <span className={`h-2 w-2 rounded-full ${TYPE_COLORS[type] ?? "bg-primary"}`} />
                    {type} ({grouped[type].length})
                  </Button>
                ))}
              </div>

              <div className="space-y-3">
                {Object.entries(grouped)
                  .filter(([type]) => typeFilter === null || type === typeFilter)
                  .sort(([, a], [, b]) => b.length - a.length)
                  .map(([type, nodes]) => {
                    const isCollapsed = collapsedGroups.has(type);
                    return (
                      <div key={type} data-testid={`node-group-${type}`} className="rounded-lg border">
                        <button
                          data-testid={`toggle-${type}`}
                          className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-muted/50"
                          onClick={() => {
                            setCollapsedGroups((prev) => {
                              const next = new Set(prev);
                              if (next.has(type)) next.delete(type);
                              else next.add(type);
                              return next;
                            });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className={`h-3 w-3 rounded-full ${TYPE_COLORS[type] ?? "bg-primary"}`} />
                            <span className="font-medium">{type}</span>
                            <Badge variant="secondary" className="text-xs">{nodes.length}</Badge>
                          </div>
                          <span className="text-sm text-muted-foreground">{isCollapsed ? "▸" : "▾"}</span>
                        </button>
                        {!isCollapsed && (
                          <div className="border-t divide-y">
                            {nodes.map((node) => (
                              <div key={node.node_id} className="px-4 py-2 text-sm">
                                <div className="flex items-center gap-3">
                                  <span className="font-medium shrink-0">{node.scope}</span>
                                  <span className="text-muted-foreground truncate">
                                    {node.content.length > 120
                                      ? `${node.content.slice(0, 120)}…`
                                      : node.content}
                                  </span>
                                </div>
                                {node.source_file && (
                                  <p className="text-xs text-muted-foreground mt-0.5">{node.source_file}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="graph">
          {detail.nodes.length === 0 ? (
            <EmptyState
              icon={Package}
              title={t("cartridgeDetail.graph.empty")}
              description={t("cartridgeDetail.graph.emptyDescription")}
            />
          ) : graphSeed ? (
            <div className="h-[calc(100vh-220px)]">
              <MemoryGraphD3
                graph={graphSeed}
                onNodeClick={setSelectedGraphNode}
                selectedNodeId={selectedGraphNode}
                className="h-full w-full"
              />
            </div>
          ) : null}
        </TabsContent>

        <TabsContent value="assignments">
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button onClick={() => setAssignOpen(true)} className="gap-1.5">
                <FolderOpen className="h-4 w-4" />
                {t("cartridgeDetail.assignments.assignButton")}
              </Button>
            </div>
            {assignmentsLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : assignments.length === 0 ? (
              <EmptyState
                icon={FolderOpen}
                title={t("cartridgeDetail.assignments.empty")}
                description={t("cartridgeDetail.assignments.emptyDescription")}
                action={{ label: t("cartridgeDetail.assignments.assignButton"), onClick: () => setAssignOpen(true) }}
              />
            ) : (
              <div className="rounded-lg border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>
                        <span className="flex items-center gap-1.5"><FolderOpen className="h-3.5 w-3.5" />{t("cartridgeDetail.assignments.table.project")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />{t("cartridgeDetail.assignments.table.policy")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><User className="h-3.5 w-3.5" />{t("cartridgeDetail.assignments.table.assignedBy")}</span>
                      </TableHead>
                      <TableHead>
                        <span className="flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{t("cartridgeDetail.assignments.table.date")}</span>
                      </TableHead>
                      <TableHead className="w-[100px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments.map((a) => (
                      <TableRow key={a.assignment_id}>
                        <TableCell className="font-medium">{a.project_slug}</TableCell>
                        <TableCell>
                          <PolicyBadge policy={a.policy} />
                        </TableCell>
                        <TableCell className="text-muted-foreground">{a.assigned_by}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(a.assigned_at)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:text-destructive"
                            onClick={() => setUnassignTarget({ projectId: a.project_id, projectSlug: a.project_slug })}
                          >
                            <PackageMinus className="h-4 w-4" />
                            {t("cartridgeDetail.assignments.unassignButton")}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cartridgeDetail.assignDialog.title")}</DialogTitle>
            <DialogDescription>{t("cartridgeDetail.assignDialog.description")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label htmlFor="project-select" className="text-sm font-medium">{t("cartridgeDetail.assignDialog.selectProject")}</label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger id="project-select" className="mt-1">
                  <SelectValue placeholder={t("cartridgeDetail.assignDialog.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {availableProjects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="policy"
                  value="optional"
                  checked={policy === "optional"}
                  onChange={() => setPolicy("optional")}
                />
                {t("cartridgeDetail.assignDialog.optional")}
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="policy"
                  value="required"
                  checked={policy === "required"}
                  onChange={() => setPolicy("required")}
                />
                {t("cartridgeDetail.assignDialog.required")}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)}>{t("cartridgeDetail.assignDialog.cancel")}</Button>
            <Button onClick={handleAssign} disabled={!selectedProject || assigning}>
              {assigning ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cartridgeDetail.assignDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={unassignTarget !== null} onOpenChange={(open) => { if (!open) setUnassignTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cartridgeDetail.unassignDialog.title", { slug: unassignTarget?.projectSlug })}</DialogTitle>
            <DialogDescription>
              {t("cartridgeDetail.unassignDialog.description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUnassignTarget(null)}>{t("cartridgeDetail.unassignDialog.cancel")}</Button>
            <Button variant="destructive" onClick={handleUnassign} disabled={unassigning}>
              {unassigning ? <Loader2 className="h-4 w-4 animate-spin" /> : t("cartridgeDetail.unassignDialog.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
